(() => {
  'use strict';

  if (window.__SMARTX_CHECKOUT_CONSENT__) return;
  window.__SMARTX_CHECKOUT_CONSENT__ = true;

  const TERMS_URL =
    '/pages/obshchi-usloviya';

  const PRIVACY_URL =
    '/pages/politika-za-zashchita-na-lichnite-danni';

  const STORAGE_KEY =
    'smartx_checkout_consent_v5';

  const TIMEZONE = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
    } catch (e) {
      return 'Unknown';
    }
  })();


  /* =====================================================
     STATE
  ===================================================== */

  const state = {
    terms: false,
    privacy: false,
    termsAt: null,
    privacyAt: null
  };

  let lastPointerToggle = {
    role: null,
    time: 0
  };

  let persistTimer = null;
  let cartConsentSaved = false;
  let checkoutInProgress = false;
  let bypassSubmit = false;


  /* =====================================================
     LANGUAGE
  ===================================================== */

  const isEnglish =
    (document.documentElement.lang || '')
      .toLowerCase()
      .startsWith('en');

  const TEXT = isEnglish
    ? {
        termsBefore: 'I agree to the ',
        terms: 'TERMS AND CONDITIONS',

        privacyBefore: 'I agree to the ',
        privacy: 'PRIVACY POLICY',

        missingBoth:
          'Please confirm the Terms and Conditions and the Privacy Policy before continuing.',

        missingTerms:
          'Please confirm the Terms and Conditions before continuing.',

        missingPrivacy:
          'Please confirm the Privacy Policy before continuing.'
      }
    : {
        termsBefore: 'Съгласие с ',
        terms: 'ОБЩИТЕ УСЛОВИЯ',

        privacyBefore: 'Съгласие за ',
        privacy: 'ОБРАБОТКА НА ДАННИ',

        missingBoth:
          'Моля, потвърдете съгласието с Общите условия и Обработката на данни, за да продължите.',

        missingTerms:
          'Моля, потвърдете съгласието с Общите условия, за да продължите.',

        missingPrivacy:
          'Моля, потвърдете съгласието с Обработката на данни, за да продължите.'
      };


  /* =====================================================
     STORAGE
  ===================================================== */

  function loadState() {
    try {
      const raw =
        sessionStorage.getItem(STORAGE_KEY);

      if (!raw) return;

      const saved =
        JSON.parse(raw);

      state.terms =
        saved.terms === true;

      state.privacy =
        saved.privacy === true;

      state.termsAt =
        typeof saved.termsAt === 'string'
          ? saved.termsAt
          : null;

      state.privacyAt =
        typeof saved.privacyAt === 'string'
          ? saved.privacyAt
          : null;

    } catch (e) {}
  }


  function saveState() {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          terms: state.terms,
          privacy: state.privacy,
          termsAt: state.termsAt,
          privacyAt: state.privacyAt
        })
      );
    } catch (e) {}
  }


  loadState();


  /* =====================================================
     TIMESTAMP
  ===================================================== */

  function newTimestamp() {
    return new Date().toISOString();
  }


  function timestampForShopify(value) {
    if (!value) return '';

    return value + ' | ' + TIMEZONE;
  }


  /* =====================================================
     SAVE TO SHOPIFY CART / ORDER
  ===================================================== */

  async function persistConsentToShopify() {

    const snapshot = {
      terms: state.terms,
      privacy: state.privacy,
      termsAt: state.termsAt,
      privacyAt: state.privacyAt
    };

    try {

      const response =
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',

          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },

          body: JSON.stringify({
            attributes: {

              /*
                DOUBLE UNDERSCORE = PRIVATE
                Няма да се показват на клиента.
              */

              __smartx_terms_accepted:
                snapshot.terms ? 'yes' : 'no',

              __smartx_terms_accepted_at:
                snapshot.terms
                  ? timestampForShopify(snapshot.termsAt)
                  : '',

              __smartx_privacy_accepted:
                snapshot.privacy ? 'yes' : 'no',

              __smartx_privacy_accepted_at:
                snapshot.privacy
                  ? timestampForShopify(snapshot.privacyAt)
                  : '',

              __smartx_consent_timezone:
                TIMEZONE,

              __smartx_consent_system:
                'SmartX checkout consent v1'
            }
          })
        });


      if (!response.ok) {
        throw new Error(
          'Consent cart update failed: ' +
          response.status
        );
      }


      /*
        Считаме съгласието за записано само
        ако междувременно клиентът не е
        променил checkbox.
      */

      if (
        snapshot.terms === state.terms &&
        snapshot.privacy === state.privacy &&
        snapshot.termsAt === state.termsAt &&
        snapshot.privacyAt === state.privacyAt
      ) {

        cartConsentSaved =
          state.terms &&
          state.privacy;

      }


      syncAllBlocks();

      return true;

    } catch (error) {

      cartConsentSaved = false;

      syncAllBlocks();

      console.warn(
        'SmartX consent could not be saved to Shopify:',
        error
      );

      return false;
    }
  }


  function schedulePersist() {

    clearTimeout(persistTimer);

    cartConsentSaved = false;

    syncAllBlocks();

    persistTimer =
      setTimeout(() => {
        persistConsentToShopify();
      }, 250);
  }


  /* =====================================================
     CSS
  ===================================================== */

  const style =
    document.createElement('style');

  style.textContent = `

    .smartx-consent-block {
      box-sizing: border-box;
      width: 100%;
      margin: 10px 0;
      font-size: 13px;
      line-height: 1.3;
    }

    .smartx-consent-options {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      align-items: start;
      width: 100%;
      column-gap: 24px;
      row-gap: 8px;
    }

    .smartx-consent-item {
      display: flex;
      align-items: flex-start;
      min-width: 0;
      margin: 0;
      gap: 7px;
      cursor: pointer;
      user-select: none;
    }

    .smartx-consent-item input[type="checkbox"] {
      display: block;
      width: 15px;
      height: 15px;
      min-width: 15px;
      margin: 1px 0 0;
      padding: 0;
      cursor: pointer;
      accent-color: #1473e6;
    }

    .smartx-consent-item span {
      display: block;
      min-width: 0;
    }

    .smartx-consent-item a {
      color: #003b8f;
      text-decoration: underline;
    }

    .smartx-consent-item a:hover {
      text-decoration: none;
    }


    /* CART */

    .smartx-consent-cart {
      margin-top: 13px;
      margin-bottom: 12px;
    }

    .smartx-consent-cart
    .smartx-consent-item {
      align-items: center;
    }


    /* POPUP */

    .smartx-consent-popup {
      margin-top: 9px;
      margin-bottom: 9px;
      font-size: 12px;
    }

    .smartx-consent-popup
    .smartx-consent-options {
      column-gap: 14px;
    }


    /* WARNING */

    .smartx-consent-warning {
      display: none;
      box-sizing: border-box;
      width: 100%;

      margin: 9px 0 1px;
      padding: 9px 11px;

      border: 1px solid #d93025;
      border-radius: 3px;

      background: #fff5f5;
      color: #b42318;

      font-size: 13px;
      line-height: 1.35;
    }

    .smartx-consent-warning.is-visible {
      display: block;
    }

    .smartx-consent-item.is-missing,
    .smartx-consent-item.is-missing a {
      color: #b42318;
    }


    /* MOBILE */

    @media (max-width: 700px) {

      .smartx-consent-options {
        grid-template-columns: 1fr;
        row-gap: 8px;
      }

    }


    /*
      GPay / accelerated checkout се показва
      чак когато:
      1. и двете са отметнати
      2. timestamp-ите са записани в Shopify
    */

    html:not(.smartx-consent-server-saved)
    #dynamic-checkout-cart,

    html:not(.smartx-consent-server-saved)
    .additional-checkout-buttons,

    html:not(.smartx-consent-server-saved)
    .cart__dynamic-checkout-buttons,

    html:not(.smartx-consent-server-saved)
    [data-shopify-buttoncontainer],

    html:not(.smartx-consent-server-saved)
    shopify-accelerated-checkout-cart,

    html:not(.smartx-consent-server-saved)
    shopify-accelerated-checkout {
      display: none !important;
    }

  `;

  document.head.appendChild(style);


  /* =====================================================
     SYNC
  ===================================================== */

  function syncAllBlocks() {

    document
      .querySelectorAll(
        '[data-smartx-consent="1"]'
      )
      .forEach(block => {

        const terms =
          block.querySelector(
            '.smartx-terms-checkbox'
          );

        const privacy =
          block.querySelector(
            '.smartx-privacy-checkbox'
          );

        if (terms) {
          terms.checked = state.terms;
        }

        if (privacy) {
          privacy.checked = state.privacy;
        }

      });


    document.documentElement
      .classList.toggle(
        'smartx-consent-complete',
        state.terms &&
        state.privacy
      );


    document.documentElement
      .classList.toggle(
        'smartx-consent-server-saved',
        state.terms &&
        state.privacy &&
        cartConsentSaved
      );
  }


  /* =====================================================
     WARNINGS
  ===================================================== */

  function clearWarnings() {

    document
      .querySelectorAll(
        '.smartx-consent-warning'
      )
      .forEach(warning => {

        warning.textContent = '';

        warning.classList.remove(
          'is-visible'
        );
      });


    document
      .querySelectorAll(
        '.smartx-consent-item'
      )
      .forEach(item => {

        item.classList.remove(
          'is-missing'
        );
      });
  }


  /* =====================================================
     TOGGLE CONSENT
  ===================================================== */

  function toggleConsent(role) {

    if (role === 'terms') {

      state.terms =
        !state.terms;

      /*
        Timestamp при съгласяване.
        Ако размаркира -> махаме timestamp-а.
        Ако маркира пак -> получава НОВ timestamp.
      */

      state.termsAt =
        state.terms
          ? newTimestamp()
          : null;

    }

    else if (role === 'privacy') {

      state.privacy =
        !state.privacy;

      state.privacyAt =
        state.privacy
          ? newTimestamp()
          : null;

    }

    else {
      return;
    }


    saveState();

    clearWarnings();

    syncAllBlocks();


    /*
      Записваме към Shopify независимо
      от Empire popup rendering-а.
    */

    schedulePersist();
  }


  /* =====================================================
     CREATE BLOCK
  ===================================================== */

  function createConsentBlock(button) {

    const block =
      document.createElement('div');

    block.className =
      'smartx-consent-block';

    block.dataset.smartxConsent =
      '1';


    if (
      button.closest('.cart-checkout')
    ) {

      block.classList.add(
        'smartx-consent-cart'
      );

    }

    else {

      block.classList.add(
        'smartx-consent-popup'
      );
    }


    block.innerHTML = `

      <div class="smartx-consent-options">


        <div
          class="smartx-consent-item smartx-terms-item"
          data-smartx-toggle="terms"
        >

          <input
            type="checkbox"
            class="smartx-terms-checkbox"
            aria-label="${TEXT.terms}"
            tabindex="0"
          >

          <span>
            ${TEXT.termsBefore}

            <a
              href="${TERMS_URL}"
              target="_blank"
              rel="noopener"
            >${TEXT.terms}</a>
          </span>

        </div>


        <div
          class="smartx-consent-item smartx-privacy-item"
          data-smartx-toggle="privacy"
        >

          <input
            type="checkbox"
            class="smartx-privacy-checkbox"
            aria-label="${TEXT.privacy}"
            tabindex="0"
          >

          <span>
            ${TEXT.privacyBefore}

            <a
              href="${PRIVACY_URL}"
              target="_blank"
              rel="noopener"
            >${TEXT.privacy}</a>
          </span>

        </div>

      </div>


      <div
        class="smartx-consent-warning"
        role="alert"
        aria-live="polite"
      ></div>

    `;


    return block;
  }


  /* =====================================================
     EMPIRE POPUP FIX
     Записваме още на pointerdown.
  ===================================================== */

  document.addEventListener(
    'pointerdown',

    event => {

      const item =
        event.target.closest &&
        event.target.closest(
          '[data-smartx-toggle]'
        );


      if (!item) return;


      /*
        Не пречим на линковете.
      */

      if (
        event.target.closest('a')
      ) {
        return;
      }


      if (
        typeof event.button === 'number' &&
        event.button !== 0
      ) {
        return;
      }


      const role =
        item.dataset.smartxToggle;


      toggleConsent(role);


      lastPointerToggle = {
        role: role,
        time: Date.now()
      };


      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

    },

    true
  );


  /* =====================================================
     CLICK FALLBACK
  ===================================================== */

  document.addEventListener(
    'click',

    event => {

      const item =
        event.target.closest &&
        event.target.closest(
          '[data-smartx-toggle]'
        );


      if (!item) return;


      if (
        event.target.closest('a')
      ) {
        return;
      }


      const role =
        item.dataset.smartxToggle;


      const cameFromPointer =
        lastPointerToggle.role === role &&
        Date.now() -
          lastPointerToggle.time < 800;


      if (!cameFromPointer) {
        toggleConsent(role);
      }


      syncAllBlocks();


      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

    },

    true
  );


  /* =====================================================
     KEYBOARD
  ===================================================== */

  document.addEventListener(
    'keydown',

    event => {

      const checkbox =
        event.target.closest &&
        event.target.closest(
          '.smartx-terms-checkbox, .smartx-privacy-checkbox'
        );


      if (!checkbox) return;


      if (
        event.key !== ' ' &&
        event.key !== 'Enter'
      ) {
        return;
      }


      const item =
        checkbox.closest(
          '[data-smartx-toggle]'
        );


      if (!item) return;


      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();


      toggleConsent(
        item.dataset.smartxToggle
      );

    },

    true
  );


  /* =====================================================
     CHECKOUT DETECTION
  ===================================================== */

  function normaliseText(element) {

    return (
      element.textContent || ''
    )
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }


  function isCheckoutElement(element) {

    if (!element) return false;


    if (
      element.matches(
        'button[name="checkout"], input[name="checkout"]'
      )
    ) {
      return true;
    }


    if (element.tagName === 'A') {

      try {

        const url =
          new URL(
            element.href,
            location.origin
          );


        if (
          url.origin === location.origin &&
          /\/checkout\/?$/i.test(
            url.pathname
          )
        ) {
          return true;
        }

      } catch (e) {}
    }


    const text =
      normaliseText(element);


    const phrases = [
      'към данните за доставка',
      'към завършване на поръчката',
      'завършване на поръчката',
      'към плащане',
      'checkout',
      'check out',
      'proceed to checkout'
    ];


    return phrases.some(
      phrase =>
        text.includes(phrase)
    );
  }


  function getCheckoutElements() {

    return Array
      .from(
        document.querySelectorAll(
          'button, input[type="submit"], a'
        )
      )
      .filter(element => {

        if (
          element.closest(
            '[data-smartx-consent="1"]'
          )
        ) {
          return false;
        }


        return isCheckoutElement(
          element
        );
      });
  }


  /* =====================================================
     INJECT
  ===================================================== */

  function injectForButton(button) {

    if (
      !button ||
      !button.parentElement
    ) {
      return;
    }


    const previous =
      button.previousElementSibling;


    if (
      previous &&
      previous.matches(
        '[data-smartx-consent="1"]'
      )
    ) {
      return;
    }


    const existing =
      Array
        .from(
          button.parentElement.children
        )
        .find(child =>

          child.matches &&

          child.matches(
            '[data-smartx-consent="1"]'
          )
        );


    if (existing) return;


    const block =
      createConsentBlock(button);


    button.parentElement.insertBefore(
      block,
      button
    );


    syncAllBlocks();
  }


  function injectBlocks() {

    getCheckoutElements()
      .forEach(
        injectForButton
      );
  }


  /* =====================================================
     FIND CONSENT BLOCK
  ===================================================== */

  function findConsentBlock(button) {

    if (!button) return null;


    const previous =
      button.previousElementSibling;


    if (
      previous &&
      previous.matches(
        '[data-smartx-consent="1"]'
      )
    ) {
      return previous;
    }


    let parent =
      button.parentElement;


    for (
      let i = 0;
      parent && i < 6;
      i++
    ) {

      const block =
        parent.querySelector(
          '[data-smartx-consent="1"]'
        );


      if (block) {
        return block;
      }


      parent =
        parent.parentElement;
    }


    return null;
  }


  /* =====================================================
     WARNING
  ===================================================== */

  function showWarning(button) {

    injectBlocks();


    const block =
      findConsentBlock(button);


    if (!block) return;


    const warning =
      block.querySelector(
        '.smartx-consent-warning'
      );

    const termsItem =
      block.querySelector(
        '.smartx-terms-item'
      );

    const privacyItem =
      block.querySelector(
        '.smartx-privacy-item'
      );


    if (!warning) return;


    termsItem.classList.toggle(
      'is-missing',
      !state.terms
    );

    privacyItem.classList.toggle(
      'is-missing',
      !state.privacy
    );


    if (
      !state.terms &&
      !state.privacy
    ) {

      warning.textContent =
        TEXT.missingBoth;

    }

    else if (!state.terms) {

      warning.textContent =
        TEXT.missingTerms;

    }

    else {

      warning.textContent =
        TEXT.missingPrivacy;
    }


    warning.classList.add(
      'is-visible'
    );


    block.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }


  /* =====================================================
     CONTINUE TO CHECKOUT
  ===================================================== */

  async function continueToCheckout(element) {

    if (checkoutInProgress) return;

    checkoutInProgress = true;


    /*
      Последен задължителен server save,
      преди клиента да напусне cart.
    */

    await Promise.race([
      persistConsentToShopify(),

      new Promise(resolve =>
        setTimeout(resolve, 1500)
      )
    ]);


    /*
      Ако е checkout link.
    */

    if (
      element &&
      element.tagName === 'A' &&
      element.href
    ) {

      window.location.assign(
        element.href
      );

      return;
    }


    /*
      Стандартен Shopify cart form.
    */

    const form =
      element
        ? (
            element.form ||
            element.closest('form')
          )
        : null;


    if (form) {

      bypassSubmit = true;


      try {

        if (
          typeof form.requestSubmit === 'function' &&
          element
        ) {

          form.requestSubmit(element);

        }

        else {

          const hidden =
            document.createElement('input');

          hidden.type = 'hidden';
          hidden.name = 'checkout';
          hidden.value = 'Checkout';

          form.appendChild(hidden);

          HTMLFormElement.prototype.submit.call(
            form
          );
        }

      } catch (e) {

        window.location.assign(
          '/checkout'
        );
      }


      return;
    }


    /*
      Empire popup fallback.
    */

    window.location.assign(
      '/checkout'
    );
  }


  /* =====================================================
     CHECKOUT CLICK
  ===================================================== */

  document.addEventListener(
    'click',

    event => {

      const clicked =
        event.target.closest &&
        event.target.closest(
          'button, input[type="submit"], a'
        );


      if (!clicked) return;


      if (
        clicked.closest(
          '[data-smartx-consent="1"]'
        )
      ) {
        return;
      }


      if (
        !isCheckoutElement(clicked)
      ) {
        return;
      }


      /*
        Липсва съгласие.
      */

      if (
        !state.terms ||
        !state.privacy
      ) {

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        showWarning(clicked);

        return;
      }


      /*
        Има съгласие.

        Спираме оригиналния checkout само
        за момент, записваме timestamp-ите,
        след което продължаваме.
      */

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();


      continueToCheckout(
        clicked
      );

    },

    true
  );


  /* =====================================================
     FORM SUBMIT
  ===================================================== */

  document.addEventListener(
    'submit',

    event => {

      if (bypassSubmit) {

        bypassSubmit = false;

        return;
      }


      const form =
        event.target;


      if (
        !(form instanceof HTMLFormElement)
      ) {
        return;
      }


      const submitter =
        event.submitter;


      let button =
        submitter;


      if (!button) {

        button =
          form.querySelector(
            'button[name="checkout"], input[name="checkout"]'
          );
      }


      if (
        !button ||
        !isCheckoutElement(button)
      ) {
        return;
      }


      if (
        !state.terms ||
        !state.privacy
      ) {

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        showWarning(button);

        return;
      }


      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();


      continueToCheckout(
        button
      );

    },

    true
  );


  /* =====================================================
     EMPIRE POPUP OBSERVER
  ===================================================== */

  let observerTimer = null;


  const observer =
    new MutationObserver(() => {

      clearTimeout(
        observerTimer
      );


      observerTimer =
        setTimeout(() => {

          injectBlocks();

          syncAllBlocks();

        }, 40);
    });


  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );


  /* =====================================================
     INIT
  ===================================================== */

  function init() {

    injectBlocks();

    syncAllBlocks();


    /*
      Ако се връща от checkout и вече има
      двете отметки в sessionStorage,
      записваме ги пак към текущата cart.
    */

    if (
      state.terms &&
      state.privacy
    ) {
      schedulePersist();
    }
  }


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true
      }
    );

  }

  else {

    init();
  }


  /* =====================================================
     DEBUG
  ===================================================== */

  window.smartxConsentStatus =
    function () {

      return {
        terms: state.terms,
        termsAt: state.termsAt,

        privacy: state.privacy,
        privacyAt: state.privacyAt,

        timezone: TIMEZONE,

        savedToShopify:
          cartConsentSaved,

        stored:
          sessionStorage.getItem(
            STORAGE_KEY
          ),

        blocks:
          document.querySelectorAll(
            '[data-smartx-consent="1"]'
          ).length
      };
    };

})();